import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  Select,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  flexRender,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";
import { useTranslation } from "react-i18next";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import Card from "../card/Card";
import { DebouncedSearch } from "./search";
import { CommonTableProps } from "./types";
import Pagination from "./Pagination";

export function CommonTable<T>({
  columns,
  data,
  totalCount,
  pageNo,
  pageSize,
  search,
  loading,
  onSearch,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  renderActions,
  handleAddNew,
  hideAddButton,
  showSearch,
  filters,
  showSelection,
  onRowSelectChange,
  showPagination,
}: CommonTableProps<T>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [rowSelection, setRowSelection] = React.useState({});

  const finalColumns = React.useMemo(() => {
    if (!showSelection) return columns;
    return [
      {
        id: "__select",
        header: ({ table }: any) => (
          <Checkbox
            isChecked={table.getIsAllRowsSelected()}
            isIndeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }: any) => (
          <Checkbox
            isChecked={row.getIsSelected()}
            isIndeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
      },
      ...columns,
    ];
  }, [columns, showSelection]);

  const table = useReactTable({
    data,
    columns: finalColumns,
    state: {
      sorting,
      rowSelection,
    },
    enableRowSelection: showSelection,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    onSortingChange: setSorting,
  });

  React.useEffect(() => {
    if (sorting.length > 0) {
      const s = sorting[0];
      onSortChange(s.id, s.desc);
    }
  }, [sorting]);

  React.useEffect(() => {
    if (onRowSelectChange) {
      const selected = table.getSelectedRowModel().rows.map((r) => r.original);
      onRowSelectChange(selected);
    }
  }, [rowSelection]);

  const textColor = useColorModeValue("gray.700", "white");

  const flexDirection =
    useBreakpointValue({ base: "column", md: "row" }) ?? "column";
  const pageCount = Math.ceil(totalCount / pageSize);
  const createPages = () => {
    let pages = [];
    for (let i = 1; i <= pageCount; i++) pages.push(i);
    return pages;
  };

  return (
    <Flex
      pt={
        !showPagination ? { base: "130px", md: "80px", xl: "80px" } : undefined
      }
    >
      <Card>
        <Flex direction="column" w="100%" overflowX="auto">
          <Flex justify="space-between" mb={10}>
            <DebouncedSearch
              value={search}
              onChange={onSearch}
              handleAddNew={handleAddNew ?? (() => {})}
              hideAddButton={hideAddButton as boolean}
              filters={filters}
              showSearch={showSearch}
            />
          </Flex>

          {loading ? (
            <Flex justify="center" my={5}>
              <Spinner color="brand.500" />
            </Flex>
          ) : (
            <Box minH="60vh" maxH="70vh" overflowY="auto">
              {table.getRowModel().rows.length > 0 ? (
                <Table variant="simple">
                  <Thead background="gray.200">
                    {table.getHeaderGroups().map((hg) => (
                      <Tr key={hg.id}>
                        {hg.headers.map((header) => (
                          <Th
                            position="sticky"
                            top={0}
                            zIndex={1}
                            background="gray.200"
                            key={header.id}
                            onClick={
                              header.column.getCanSort()
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                            cursor={
                              header.column.getCanSort() ? "pointer" : "default"
                            }
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {!header.column.getIsSorted() &&
                              header.column.columnDef.sortDescFirst &&
                              " 🔽"}

                            {{
                              asc: " 🔼",
                              desc: " 🔽",
                            }[header.column.getIsSorted() as string] ?? null}
                          </Th>
                        ))}
                      </Tr>
                    ))}
                  </Thead>
                  <Tbody>
                    {table.getRowModel().rows.map((row) => (
                      <Tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <Td key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Td>
                        ))}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Text
                  fontSize="sm"
                  color={textColor}
                  fontWeight="normal"
                  textAlign={"center"}
                  marginRight={20}
                >
                  {t("common.list.noRecord")}
                </Text>
              )}

              {data?.length !== 0 && !loading && !showPagination && (
                <Pagination
                  pageNo={pageNo}
                  totalCount={totalCount}
                  pageSize={pageSize}
                  onPageChange={onPageChange}
                  onPageSizeChange={onPageSizeChange}
                />
              )}
            </Box>
          )}
        </Flex>
      </Card>
    </Flex>
  );
}
